// CPL A2 — 취득세 세율표·법정 산식은 `lib/calc/gov-tables.ts` 한 곳이다. 여기서 만들지 말 것.
import { acqSurtaxPct, acqTaxMidRatePct } from '@/lib/calc/gov-tables';
// CPL A-3 — 평당가 산식은 `lib/apt/supply-price.ts` 한 곳이다. 여기서 만들지 말 것.
import { pyeongPrice } from '@/lib/apt/supply-price';

/**
 * 관심단지 표시 유틸리티 — 정직한 소셜프루프
 * 
 * 원칙: 허수 없음. 실 데이터만 표시.
 * - 관심등록 있으면 → 실제 수치 표시
 * - 관심등록 0이면 → 조회수 또는 행동유도 문구 표시
 */

/**
 * 취득세 — **세율표는 `lib/calc/gov-tables.ts` 한 곳이다** (CPL A2 · 2026-09-23).
 *
 * ── 무엇이 틀렸나 ──
 * 이 함수는 자기 세율표를 들고 있었고, 그 표가 정본과 «달랐다»:
 *
 *   · 농어촌특별세를 `취득세액 × 20%` 로 계산했다. 법정은 «과세표준 × 0.2%» 다.
 *     세율 1% 구간에서만 우연히 같고, 9억 초과(3%)·85㎡ 초과에서는 **3배 과대** 였다.
 *     (`gov-tables.ts:287` 주석이 이 결함형을 이미 「옛 코드」로 지목해 두었는데,
 *      그 수리가 이 파일과 `cron/apt-parse-pdf-pricing` 에는 닿지 않았다.)
 *   · 6~9억 구간을 자체 선형보간으로 냈다. 정본은 지방세법 §11①8 후단의 «넷째 자리
 *     반올림» 까지 포함한 산식이다(7억 → 1.6667%, 이 파일은 1.66667%).
 *
 * 그래서 **표를 새로 만들지 않고 정본에 연결했다.** 여기 남은 것은 단위 변환뿐이다.
 *
 * ── 이 함수가 «말하지 않는» 것 ──
 * ⚠️ 1주택·비조정 표준세율 기준이다. 중과(8%·12%)·감면(생애최초·지방 미분양)·증여·상속은
 *    `lib/calc/k9/local.ts` 의 `acquisitionParts` 가 맡고, 그쪽은 `policy_constants` 주입을
 *    요구한다(클라이언트 번들로 끌어올 수 없다). 호출 화면이 「1주택 기준」을 명시한다
 *    (`CostSimulator.tsx`). 그 전제를 넓히려면 정책팩 전달이 선행이고, 별건이다.
 * ⛔ 전제를 감춘 채 중과를 «추정» 하지 않는다 — 주택 수를 모르는 화면이 중과를 말하면
 *    그게 오답이다.
 *
 * @param priceManWon   과세표준(만원). 호출부가 고른 타입·층의 분양가.
 * @param exclusiveAreaSqm 전용면적(㎡). 농특세는 85㎡ 초과에만 붙는다.
 */
export function calcAcquisitionTax(
  priceManWon: number,
  exclusiveAreaSqm: number
): { tax: number; education: number; rural: number; total: number } {
  // ⚠️ 정본 함수는 «원» 을, 이 화면은 «만원» 을 쓴다. 경계 상수를 여기서 다시 적지 않는다.
  const priceWon = priceManWon * 10_000;

  // 본세율(%) — 6억 이하 1 · 6~9억 법정 산식 · 9억 초과 3.
  const baseRatePct =
    priceWon <= 600_000_000 ? 1
    : priceWon <= 900_000_000 ? acqTaxMidRatePct(priceWon)
    : 3;

  // 부가세목(%) — «과세표준 대비» 다. 본세액 대비가 아니다.
  const { eduPct, farmPct } = acqSurtaxPct(baseRatePct, 'none', exclusiveAreaSqm > 85);

  const tax = Math.round(priceManWon * baseRatePct / 100);
  const education = Math.round(priceManWon * eduPct / 100);
  const rural = Math.round(priceManWon * farmPct / 100);
  const total = tax + education + rural;

  return { tax, education, rural, total };
}

/**
 * 평당가 계산 (만원 단위).
 *
 * CPL A-3 «잔여» (2026-09-23): 종전엔 `Math.round(price / (area / 3.3058))` 을 **여기에 또**
 * 적어 두었다 — `supply-price.pyeongPrice()` 와 상수·반올림까지 같은 쌍둥이였고, 다른 건
 * 면적 하한 가드(`> 10㎡`)가 없다는 것뿐이었다. 상수가 한 자리에서만 고쳐질 위험을 없애려고
 * 정본으로 위임한다. ⛔ 3.3058 을 여기 다시 적지 말 것 — `PYEONG_SQM` 이 정본이다.
 *
 * ⚠️ 넘기는 면적이 전용인지 공급인지는 **부르는 쪽 책임** 이다(정본과 같은 계약).
 *    이 함수의 호출부(`CostSimulator`)는 주택형 문자열에서 뽑은 **전용면적** 을 넘긴다.
 * ⚠️ 반환 계약은 종전대로 `number` 다(값이 안 서면 `0`). 정본도 0 을 내므로 그대로 통한다.
 */
export function calcPricePerPyeong(priceManWon: number, exclusiveAreaSqm: number): number {
  return pyeongPrice(priceManWon, exclusiveAreaSqm);
}
